import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { useDebounce } from '@/hooks/useDebounce';
import { hasAnyPermission } from '../utils/permissions';


import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

import { Plus, Search, UserCog, Edit, Mail, Shield, Users, Phone, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { generateId } from '../utils/dateUtils';
import { isSuperAdmin, isClinicAdmin } from '../utils/permissions';
import { toast } from 'react-toastify';
import { useListStaff, useAddStaff, useUpdateStaff, useStatusStaff, useStaffPerm, useShowStaffData} from '@/hooks/useApi';


const StaffManagement = () => {
  // State declarations first
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [statusToggleData, setStatusToggleData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);


  // Hooks that depend on state
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const { data: staffData, isLoading: isLoadingStaff, refetch: refetchStaff } = useListStaff(debouncedSearchQuery, roleFilter, currentPage);
  const { mutate: addStaff, isPending: isAddingStaff } = useAddStaff();
  const { mutate: updateStaff, isPending: isUpdatingStaff } = useUpdateStaff();
  const { mutate: toggleStatus, isPending: isToggling } = useStatusStaff();



  useEffect(() => {
    if (staffData) {
      setTotalPages(staffData.data.total_pages);
      setCurrentPage(staffData.data.current_page);
    }
  }, [staffData]);

  const { currentUser } = useAuth();
  const navigate = useNavigate();

 
  // Define the simplified permissions


  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    phone: '',
   
    permissions: [],
  });

  

  // Search is now handled by API, no need for client-side filtering

  // Fetch staff data when editing
  const {
    data: getstaffdata,
    isLoading: isLoadingStaffData,
    error: errorStaffData,
    dataUpdatedAt,
  } = useShowStaffData(editingUser?.id, {
    enabled: !!editingUser && isDialogOpen, // ✅ key fix
  });
  

  // Populate formData when API data is loaded
  useEffect(() => {
    if (getstaffdata && editingUser && editingUser.id && isDialogOpen) {
      const staffData = getstaffdata?.data?.data || getstaffdata?.data || getstaffdata;
      
      // Only update if we have valid data
      if (staffData && (staffData.name || staffData.email)) {
        const userPermissions = staffData.permissions || [];
        // Convert permissions to strings if they're objects
        const validPermissions = userPermissions.map(p => 
          typeof p === 'string' ? p : (p.name || p.key || p.id || p)
        );
        
        // Always update form when dialog is open and we have data
        // This ensures form is populated even with cached data
        setFormData({
          name: staffData.name || '',
          email: staffData.email || '',
          role: String(staffData.role || ''),
          phone: staffData.phone || '',
          permissions: validPermissions,
        });
      }
    }
  }, [getstaffdata, editingUser?.id, isDialogOpen, dataUpdatedAt]);
  



  

  // Client-side filtering removed - API handles search now
  // Role filter can be added to API later if needed

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenDialog = async (user = null) => {
    if (user) {
      // Reset formData first
      setFormData({
        name: '',
        email: '',
        role: '',
        phone: '',
        permissions: [],
      });
      
      // Set editing user - this will trigger the query
      setEditingUser(user);
      setIsDialogOpen(true);
      
      
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        role: '',
        phone: '',
        permissions: [],
      });
      setIsDialogOpen(true);
    }
  };
  

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: '',
      phone: '',
   
      permissions: [],
    });
  };

  const handleSubmit = () => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
        if (!emailRegex.test(formData.email)) {
          toast.error('Please enter a valid email address');
          return;
        }

    if (!formData.name || !formData.email || !formData.role || !formData.phone) {
      toast.error('Missing Information', {
        title: 'Missing Information',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

  

    const staffPayload = {
      name: formData.name,
      email: formData.email,
      role: formData.role,
      phone: formData.phone,
      permissions: formData.permissions || [],
    };

    if (editingUser) {
      // Update staff
      updateStaff(
        { id: editingUser.id, ...staffPayload },
        {
          onSuccess: () => {
            toast.success(`${formData.name} has been updated successfully`);
            refetchStaff();
            handleCloseDialog();
          },
          onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update staff');
          },
        }
      );
    } else {
      // Add new staff
      addStaff(staffPayload, {
        onSuccess: () => {
          toast.success(`${formData.name} has been added successfully`);
          refetchStaff();
          handleCloseDialog();
        },
        onError: (error) => {
          toast.error(error.response?.data?.message || 'Failed to add staff');
        },
      });
    }
  };


  const handlePermissionToggle = (permission) => {
    setFormData((prev) => {
      const currentPermissions = prev.permissions || [];
      // Handle both string permissions and object permissions with name/id
      // For API, we need to use the permission key/name (e.g., "clinic.list")
      const permissionValue = typeof permission === 'string' 
        ? permission 
        : (permission.name || permission.key || permission.id || permission);
      const hasPermission = currentPermissions.includes(permissionValue);
      
      if (hasPermission) {
        return {
          ...prev,
          permissions: currentPermissions.filter((p) => p !== permissionValue),
        };
      } else {
        return {
          ...prev,
          permissions: [...currentPermissions, permissionValue],
        };
      }
    });
  };

  // Group permissions by category (e.g., "clinic", "patient", "photo")
  const groupPermissionsByCategory = (permissions) => {
    const grouped = {};
    
    permissions.forEach((permission) => {
      // Handle both string and object permissions
      // Use permission.key or permission.name to get the actual permission string
      const permName = typeof permission === 'string' 
        ? permission 
        : (permission.key || permission.name || permission.id || permission);
      const category = permName.split('.')[0] || 'other'; // Get prefix before dot (e.g., "clinic" from "clinic.list")
      
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(permission);
    });
    
    return grouped;
  };

  // Format permission name for display
  const formatPermissionName = (permission) => {
    // If permission is a string (display_name), use it directly
    if (typeof permission === 'string') {
      return permission;
    }
    
    // If permission is an object, use display_name, name, or key
    if (permission?.display_name) {
      return permission.display_name;
    }
    
    // Fallback to name, key, or id
    const permValue = permission?.name || permission?.key || permission?.id || permission;
    
    // If it's a dot-separated string like "clinic.list", format it
    if (typeof permValue === 'string' && permValue.includes('.')) {
      const parts = permValue.split('.');
      if (parts.length > 1) {
        return parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
      }
    }
    
    return permValue || 'Unknown Permission';
  };

  // Get category display name
  const getCategoryName = (category) => {
    const categoryNames = {
      clinic: 'Clinic',
      patient: 'Patient',
      photo: 'Photo',
      staff: 'Staff',
      other: 'Other',
    };
    return categoryNames[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Check if category is expanded
  const isCategoryExpanded = (category) => {
    return expandedCategories[category] == false; // Default to expanded
  };


  

  const handleToggleStatus = (userId, currentStatus) => {
    // Show confirmation dialog first
    setStatusToggleData({ userId, currentStatus });
    setIsStatusDialogOpen(true);
  };

  const confirmStatusToggle = () => {
    if (!statusToggleData) return;
    
    const { userId, currentStatus } = statusToggleData;
    toggleStatus(userId, {
      onSuccess: () => {
        toast.success(`Staff member has been ${currentStatus === 1 ? 'deactivated' : 'activated'}`);
        refetchStaff();
        setIsStatusDialogOpen(false);
        setStatusToggleData(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update staff status');
        setIsStatusDialogOpen(false);
        setStatusToggleData(null);
      },
    });
  };

  const { data: staffPermData } = useStaffPerm();




  const getRoleIcon = (role) => {
    switch (role) {
      case 'superadmin':
        return Shield;
      case 'clinic_admin':
        return UserCog;
      case 'staff':
        return Users;
      default:
        return Users;
    }
  };
  
  const staffUsers = staffData?.data?.data || [];
  const from = staffData?.data?.from;






  // TODO: Integrate API when available
  // const clinics = await getClinicsApi();
  const clinics = [];
  const uniqueRoles = [...new Set(staffUsers.map((u) => u.role_name || u.role))];

  const getPageStyle = (page) => {
    const intensity = page / totalPages;
  
    if (page === currentPage) {
      return 'bg-primary-600 text-white scale-110';
    }
  
    if (intensity < 0.4) return 'bg-gray-100 text-gray-600';
    if (intensity < 0.7) return 'bg-gray-200 text-gray-700';
    return 'bg-gray-300 text-gray-800';
  };
  

  return (
    <div className="space-y-6 animate-fadeIn overflow-hidden">
      {/* Header */}
     
      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Role Filter */}
            <div className="">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem >All Roles</SelectItem>
                  
                  <SelectItem value="2">Doctor</SelectItem>
                  <SelectItem value="3">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
    {hasAnyPermission(currentUser, ['staff.create','all']) && (
            <div>
             
            <Button
           
          onClick={() => handleOpenDialog()}
          className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          
          Add Staff
        </Button>
        
            
            </div>
    )}

            {/* <div>
            <p className="text-gray-600 text-end mt-1">{staffUsers.length} staff member(s) found</p>
            </div> */}
          </div>
        </CardContent>
      </Card>

      {/* Staff List Table */}
      {staffUsers.length === 0 ? (
        <Card>
          <CardContent className="p-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserCog className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No staff members found</h3>
              <p className="text-gray-600 mb-6">Get started by adding your first staff member</p>
             {hasAnyPermission(currentUser, ['staff.create','all']) && (
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Staff
              </Button>
             )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="w-[95vw] sm:w-auto overflow-x-auto">
          <CardContent>
            <Table className="">
              <TableHeader>
                <TableRow>
                  <TableHead className='text-center'>
                    <div className="flex items-center justify-center gap-2">
                      <span>Sr. No.</span>
                    </div>
                  </TableHead>
                  <TableHead className='text-center'>
                    <div className="flex items-center justify-center gap-2">
                      <span>Name</span>
                      <Users className="w-4 h-4 text-gray-400" />
                    </div>
                  </TableHead>
                  <TableHead className='text-center'>
                    <div className="flex items-center justify-center gap-2">
                      <span>Email</span>
                      <Mail className="w-4 h-4 text-gray-400" />
                    </div>
                  </TableHead>
                  <TableHead className='text-center'>
                    <div className="flex items-center justify-center gap-2">
                      <span>Phone</span>
                      <Phone className="w-4 h-4 text-gray-400" />
                    </div>
                  </TableHead>
                  <TableHead className='text-center'>
                    <div className="flex items-center justify-center gap-2">
                      <span>Role</span>
                      <UserCog className="w-4 h-4 text-gray-400" />
                    </div>
                  </TableHead>
                  <TableHead className='text-center'>
                    <div className="flex items-center justify-center gap-2">
                      <span>Created</span>
                      <Calendar className="w-4 h-4 text-gray-400" />
                    </div>
                  </TableHead>
                  {hasAnyPermission(currentUser, ['staff.update','all']) && (
                  <TableHead className="text-center">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {staffUsers.map((user, index) => {
                  const RoleIcon = getRoleIcon(user.role);
                  return (
                    <TableRow key={user.id}>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-3">
                          <span className="text-black text-xs sm:text-sm">{from + index}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-center">
                        <div className="flex items-center justify-center gap-3">
                          {user.name}
                        </div>
                      </TableCell>
                      <TableCell className='text-center'>
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-sm">{user.email}</span>
                        </div>
                      </TableCell>
                      <TableCell className='text-center'>
                        <div className="flex items-center justify-center">
                          <span className="text-sm text-gray-600">{user.phone || 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell className='text-center'>
                        <div className="flex items-center justify-center">
                          <span className="text-sm text-gray-600">{user.role_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className='text-center'>
                        <div className="flex items-center justify-center">
                          <span className="text-sm text-gray-600">{formatDate(user.created_at)}</span>
                        </div>
                      </TableCell>
                      {hasAnyPermission(currentUser, ['staff.update','all',]) && (
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          {hasAnyPermission(currentUser, ['staff.update','all']) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(user)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          )}
                          {hasAnyPermission(currentUser, ['staff.update','all']) && (
                          <Switch
                            checked={user.is_active === 1}
                            onCheckedChange={() => handleToggleStatus(user.id, user.is_active)}
                            disabled={isToggling}
                          />
                          )}
                        </div>
                      </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
          {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
  <Button
    variant="outline"
    disabled={currentPage === 1}
    onClick={() => setCurrentPage((p) => p - 1)}
  >
    Prev
  </Button>

  {Array.from({ length: totalPages }, (_, i) => {
    const page = i + 1;
    return (
      <button
        key={page}
        onClick={() => setCurrentPage(page)}
        className={`
          h-9 w-9 rounded-full transition-all
          ${getPageStyle(page)}
        `}
      >
        {page}
      </button>
    );
  })}

  <Button
    variant="outline"
    disabled={currentPage === totalPages}
    onClick={() => setCurrentPage((p) => p + 1)}
  >
    Next
  </Button>
</div>
          )}

        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
            <DialogDescription>
              {editingUser
                ? 'Update the staff member information below.'
                : 'Enter the details for the new staff member.'}
            </DialogDescription>
          </DialogHeader>
          {isLoadingStaffData && editingUser ? (
            <>
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Loading staff data...</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
              </DialogFooter>
            </>
          ) : errorStaffData ? (
            <>
              <div className="text-center py-8">
                <p className="text-sm text-red-600">Failed to load staff data. Please try again.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
          <div className=" grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                placeholder="Enter full name"
                value={formData.name}
                onKeyPress={(e) => {
                  if (!/^[a-zA-Z ]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
              <Input
                id="email"
                type="email"
                onKeyPress={(e) => {
                  if (!/^[a-zA-Z0-9@._-]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                
                placeholder="staff@example.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone <span className="text-red-500">*</span></Label>
              <Input
                id="phone"
                type="tel"
                maxLength={10}
                placeholder="9999999999"
                onKeyPress={(e)=>{
                  if (!/^\d*$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </div>

             <div className="space-y-2 ">
               <Label htmlFor="role">Role <span className="text-red-500">*</span></Label>
               <Select value={formData.role || undefined} onValueChange={(value) => handleInputChange('role', value)}>
                 <SelectTrigger>
                   <SelectValue placeholder="Select role" />
                 </SelectTrigger>
                 <SelectContent>
                   <SelectItem value="2">Doctor</SelectItem>
                   <SelectItem value="3">Staff</SelectItem>
                 </SelectContent>
               </Select>
             </div>
            

            {/* Permissions Section */}
        <div className="col-span-2">
              <div className="space-y-4 pt-4 border-t">
                <Label className="text-base font-semibold">Permissions</Label>
                {staffPermData?.data?.permissions && staffPermData.data.permissions.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {Object.entries(groupPermissionsByCategory(staffPermData.data.permissions)).map(([category, perms]) => {
                      const isExpanded = isCategoryExpanded(category);
                      const selectedCount = perms.filter(p => {
                        const permValue = typeof p === 'string' ? p : (p.name || p.id || p);
                        return formData.permissions?.includes(permValue);
                      }).length;
                      
                      return (
                        <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleCategory(category)}
                            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-600" />
                                
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                              )}
                              <Label className="text-sm font-semibold text-gray-700 uppercase tracking-wide cursor-pointer">
                                {getCategoryName(category)}
                              </Label>
                              {selectedCount > 0 && (
                                <Badge variant="secondary" className="ml-2">
                                  {selectedCount} selected
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-gray-500">{perms.length} permissions</span>
                          </button>
                          {isExpanded && (
                            <div className="p-3 bg-white">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {perms.map((permission) => {
                                  // Get the permission key/name for API (e.g., "clinic.list")
                                  const permissionValue = typeof permission === 'string' 
                                    ? permission 
                                    : (permission.key || permission.name || permission.id || permission);
                                  const isChecked = formData.permissions?.includes(permissionValue) || false;
                                  return (
                                    <div
                                      key={permissionValue}
                                      className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 border border-gray-100"
                                    >
                                      <Label
                                        htmlFor={`perm-${permissionValue}`}
                                        className="text-sm font-medium text-gray-700 cursor-pointer flex-1"
                                      >
                                        {formatPermissionName(permission)}
                                      </Label>
                                      <Switch
                                        id={`perm-${permissionValue}`}
                                        checked={isChecked}
                                        onCheckedChange={() => handlePermissionToggle(permission)}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No permissions available
                  </div>
                )}
              </div>
              </div>
         
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isAddingStaff || isUpdatingStaff || (isLoadingStaffData && editingUser)}
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAddingStaff || isUpdatingStaff 
                ? (editingUser ? 'Updating...' : 'Adding...') 
                : (editingUser ? 'Update' : 'Add')} Staff
            </Button>
          </DialogFooter>
          </>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Toggle Confirmation Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Status Change</DialogTitle>
            <DialogDescription>
              Are you sure you want to {statusToggleData?.currentStatus === 1 ? 'deactivate' : 'activate'} this staff member?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsStatusDialogOpen(false);
              setStatusToggleData(null);
            }}>
              Cancel
            </Button>
            <Button
              onClick={confirmStatusToggle}
              disabled={isToggling}
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
            >
              {isToggling ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;


